import { useState } from 'react';
import { useSiteContent } from '@/context/SiteContentContext';

const categories = ['All', 'GIS & Surveying', 'Research & Evaluation', 'IT Services', 'Training & Capacity Building'];

export default function Projects() {
  const { content } = useSiteContent();
  const projects = content.projects;
  const [activeCategory, setActiveCategory] = useState('All');
  const [selected, setSelected] = useState<string | null>(null);

  const filtered = activeCategory === 'All' ? projects : projects.filter((p) => p.category === activeCategory);
  const selectedProject = projects.find((p) => p.id === selected);

  if (projects.length === 0) return null;

  return (
    <section id="projects" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="mb-12">
          <span className="text-teal-600 text-sm font-semibold tracking-widest uppercase">Our Work</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">
            Project <span className="text-teal-600">Gallery</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl">
            A showcase of our completed projects across GIS, IT, and research disciplines throughout East Africa.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project) => (
            <div
              key={project.id}
              onClick={() => setSelected(project.id)}
              className="group rounded-2xl overflow-hidden border border-gray-100 hover:border-teal-200 transition-all cursor-pointer bg-white hover:scale-[1.02] duration-300"
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden bg-gray-100">
                {project.image ? (
                  <>
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-teal-950/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 border-b border-gray-100">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-50 mb-3">
                      <i className="ri-image-add-line text-teal-400 text-2xl"></i>
                    </div>
                    <span className="text-gray-400 text-xs font-medium">Photo coming soon</span>
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <span className="bg-teal-600/90 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {project.category}
                  </span>
                </div>
                {project.image && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                      <i className="ri-zoom-in-line text-white text-lg"></i>
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-teal-600 text-xs font-semibold">{project.year}</span>
                  <span className="text-gray-400 text-xs">{project.client}</span>
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-2 leading-snug">{project.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">{project.description}</p>
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-folder-open-line text-4xl mb-3 block"></i>
            <p className="text-sm">No projects in this category yet.</p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selected && selectedProject && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-2xl overflow-hidden max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-64">
              {selectedProject.image ? (
                <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-full object-cover object-center" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal-50 mb-3">
                    <i className="ri-image-add-line text-teal-400 text-3xl"></i>
                  </div>
                  <span className="text-gray-400 text-sm font-medium">Photo coming soon</span>
                </div>
              )}
              <button
                onClick={() => setSelected(null)}
                className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
              <div className="absolute top-3 left-3">
                <span className="bg-teal-600 text-white text-xs font-semibold px-3 py-1 rounded-full">{selectedProject.category}</span>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-teal-600 text-sm font-semibold">{selectedProject.year}</span>
                <span className="text-gray-300">•</span>
                <span className="text-gray-500 text-sm">{selectedProject.client}</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{selectedProject.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{selectedProject.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
